import axios, { type AxiosInstance, type AxiosResponse } from 'axios';

import type { ApiCouponsFilters, ApiStakeItem, ApiStakesFilters } from '@/types/stakes';
import { createParams } from '@/utils/axios';
import { localDateToUTC } from '@/utils/date';
import type { User } from '@/stores/users';
import type { AvailableLanguage } from '@/stores/translations';

// @ts-ignore not in project deps
import type { ResultSet } from '../../../../libs/bet-genius/src/common/result-set/result-set.types';
import type { CreatePool, CreatePoolType, CreatePoolTypeCategory, UpdatePool } from '@/types/pools';

const devUrl = import.meta.env.VITE_ADMIN_API_URL || 'https://dev.coins-sport.com/api';
const prodUrl = import.meta.env.VITE_ADMIN_API_URL || '/api';
const baseURL = import.meta.env.MODE === 'production' ? prodUrl : devUrl;

let instance: AxiosInstance = axios.create({ baseURL });

export const setAuthToken = (token: string | null) => {
  const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

  instance = axios.create({ baseURL, headers });
};

export const fetchHealth = async () => instance.get('health').then((response) => response.data);

export const fetchStakesCount = async (filters: ApiStakesFilters) => {
  const params = {
    skipEmpty: filters.skipEmpty,
    fromDate: localDateToUTC(filters.fromDate, 0)?.toISOString(),
    toDate: localDateToUTC(filters.toDate)?.toISOString(),
    period: filters.period,
  };

  const query = createParams(params);

  return instance.get(`bets/count?${query}`).then((response) => response.data);
};

export const fetchStakes = async ({ offset = 0, size = 20 }, filters: ApiStakesFilters) => {
  const params = {
    offset,
    count: size,
    skipEmpty: filters.skipEmpty || undefined,
    skipBanned: filters.skipBanned || undefined,
    skipNonCurrency: filters.skipNonCurrency || undefined,
    fromDate: localDateToUTC(filters.fromDate, 0)?.toISOString(),
    toDate: localDateToUTC(filters.toDate)?.toISOString(),
    detailSports: filters.detailSports === 'true' || undefined,
    detailCurrencies: filters.detailCurrencies === 'true' || undefined,
    detailUsers: filters.detailUsers === 'true' || undefined,
    period: filters.period,
    sportIds: filters.sportIds ?? undefined,
    userIds: filters.userIds ?? undefined,
    currencies: filters.currencies ?? undefined,
  };

  const query = createParams(params);

  return instance
    .get(`bets/aggregate?${query}`)
    .then((response: AxiosResponse<ApiStakeItem[]>) => response.data);
};

export const checkToken = async (): Promise<string[]> =>
  instance?.defaults.headers.Authorization
    ? instance.get('token/check').then((response) => response.data)
    : Promise.reject('No checks on empty token');

export const getToken = async (sid: string) =>
  instance.get(`token/${sid}`).then((response: AxiosResponse<string>) => response.data);

export const login = async (name: string, secret: string) =>
  instance
    .post('login', { name, secret })
    .then((response: AxiosResponse<{ token: string; setup?: boolean }>) => response.data);

export const setPassword = async (
  name: string,
  secret: string,
  password1: string,
  password2: string
) =>
  instance.patch('login', { name, secret, password1, password2 }).then((response) => response.data);

export const fetchUserSections = async () =>
  instance.get('users/my/sections').then((response) => response.data);

export const fetchCoupons = async ({ offset = 0, size = 20 }, filters: ApiCouponsFilters) => {
  const params = {
    offset,
    count: size,
    fromDate: localDateToUTC(filters.fromDate, 0)?.toISOString(),
    toDate: localDateToUTC(filters.toDate)?.toISOString(),
    userIds: filters.userIds ?? undefined,
    currencies: filters.currencies ?? undefined,
  };

  const query = createParams(params);

  return instance.get(`coupons?${query}`).then((response) => response.data);
};

export const fetchCouponsCount = async (filters: ApiCouponsFilters) => {
  const params = {
    fromDate: localDateToUTC(filters.fromDate, 0)?.toISOString(),
    toDate: localDateToUTC(filters.toDate)?.toISOString(),
    userIds: filters.userIds ?? undefined,
    currencies: filters.currencies ?? undefined,
  };

  const query = createParams(params);

  return instance.get(`coupons/count?${query}`).then((response) => response.data);
};

export const fetchCoupon = async (id: string) =>
  instance.get(`coupons/${id}`).then((response) => response.data);

export const fetchSports = async (lang: string) =>
  instance.get(`sports/${lang}`).then((response) => response.data);

export const createUser = async (name: string, description: string) =>
  instance.post('users', { name, description });

export const fetchUsers = async () => instance.get('/users').then((response) => response.data);

export const fetchSections = async () =>
  instance.get('/sections').then((response) => response.data);

export const postUserAcl = async (user: User) =>
  instance
    .post(`users/${user.id}/acl`, {
      sections: user.UserToSection.map((aclEntry) => ({
        id: aclEntry.section.id,
        mode: aclEntry.write ? 'w' : aclEntry.read ? 'r' : null,
      })),
    })
    .then((response) => response.data);

export const patchBlockUser = async (id: number) =>
  instance.patch(`users/${id}/block`).then((response) => response.data);

export const setUserDescription = async (id: number, description: string) =>
  instance.patch(`users/${id}/description`, { description }).then((response) => response.data);

export const resetPassword = async (id: number) =>
  instance.get(`users/${id}/reset`).then((response) => response.data);

export const setSportPriority = async (lang: string, sportId: number, order: number | null) =>
  instance.post(`sports/${lang}/${sportId}`, { order }).then((response) => response.data);

export const copyPriorities = async (fromLang: string, toLang: string) =>
  instance
    .patch(`sports`, {
      from: fromLang,
      to: toLang,
    })
    .then((response) => response.data);

// Favorites and Popular
export const fetchFavoritesSports = async () =>
  instance.get('favorites/sports').then((response) => response.data);

export const fetchFavoritesRegions = async () =>
  instance.get('favorites/regions').then((response) => response.data);

export const fetchPopularCompetitions = async () =>
  instance.get('favorites/popular/competitions').then((response) => response.data);

export const deletePopularCompetition = async (id: number) =>
  instance.delete(`favorites/popular/competitions/${id}`).then((response) => response.data);

export const addPopularCompetition = async (id: number) =>
  instance.patch(`favorites/popular/competitions/${id}`).then((response) => response.data);

export const fetchPopularFixtures = async () =>
  instance.get('favorites/popular/fixtures').then((response) => response.data);

export const deletePopularFixture = async (id: number) =>
  instance.delete(`favorites/popular/fixtures/${id}`).then((response) => response.data);

export const addPopularFixture = async (id: number) =>
  instance.patch(`favorites/popular/fixtures/${id}`).then((response) => response.data);

export const fetchFavoritesSourceCompetitions = async (filters?: any) => {
  const query = filters ? createParams(filters) : '';
  return instance.get(`favorites/source/competitions?${query}`).then((response) => response.data);
};

export const fetchFavoritesSourceFixtures = async (filters?: any) => {
  const query = filters ? createParams(filters) : '';
  return instance.get(`favorites/source/fixtures?${query}`).then((response) => response.data);
};

// Translations
export const fetchTranslations = async (filters?: any) => {
  const query = filters ? createParams(filters) : '';
  return instance.get(`translations?${query}`).then((response) => response.data);
};

export const updateTranslation = async (id: string, update: any) =>
  instance.patch(`translations/${id}`, update).then((response) => response.data);

export const removeTranslation = async (locale: string, id: string) =>
  instance.delete(`translations/${id}/${locale}`).then((response) => response.data);

export const deleteTranslation = async (type: string, id: string) =>
  instance.delete(`translations/delete/${type}/${id}`).then((response) => response.data);

export const toggleApproval = async (id: string, locale: AvailableLanguage) =>
  instance.patch(`translations/approve/${id}/${locale}`);

export const fetchInProgressFixtures = async (filters?: any) => {
  const query = filters ? createParams(filters) : '';
  return instance.get(`fixtures/in-progress?${query}`).then((response) => response.data);
};

export const finishFixture = async (id: number) =>
  instance.patch(`fixtures/${id}/finish`).then((response) => response.data);

export const fetchFixture = async (id: number) =>
  instance.get(`fixtures/${id}`).then((response) => response.data);

export const postMarketResolution = async (resultSet: ResultSet) =>
  instance.post(`market/resolve`, resultSet).then((response) => response.data);

export const fetchBets = async (filters: any) => {
  const query = filters ? createParams(filters) : '';
  return instance.get(`bets?${query}`).then((response) => response.data);
};

export const reSettleBet = async (guid: string | number) =>
  instance.get(`bets/resettle/${guid}`).then((response) => response.data);

export const refundBrokenBet = async (guid: string) =>
  instance.get(`bets/refund-broken/${guid}`).then((response) => response.data);

export const repayBrokenBet = async (guid: string) =>
  instance.get(`bets/repay-broken/${guid}`).then((response) => response.data);

export const rollBackBet = async (guid: string) =>
  instance.get(`bets/roll-back/${guid}`).then((response) => response.data);

export const resetMarket = async (marketId: number) =>
  instance.get(`market/reset/${marketId}`).then((response) => response.data);

export const fetchProfile = async (id: number) =>
  instance.get(`profile/${id}`).then((response) => response.data);

export const fetchPoolTypes = async (filters?: any) => {
  const query = filters ? createParams(filters) : '';
  return instance.get(`pools/types?${query}`).then((response) => response.data);
};

export const fetchPoolTypeIds = async () =>
  instance.get(`pools/type-ids`).then((response) => response.data);

export const createPoolType = async (record: CreatePoolType) =>
  instance.post('pools/types', record).then((response) => response.data);

export const deletePoolType = async (id: number) =>
  instance.delete(`pools/types/${id}`).then((response) => response.data);

export const fetchPoolTypeCategories = async (id: number, filters?: any) => {
  const query = filters ? createParams(filters) : '';
  return instance.get(`pools/types/${id}/categories?${query}`).then((response) => response.data);
};

export const createPoolTypeCategory = async (id: number, record: CreatePoolTypeCategory) =>
  instance.post(`pools/types/${id}/categories`, record).then((response) => response.data);

export const deletePoolTypeCategory = async (id: number) =>
  instance.delete(`pools/types/categories/${id}`).then((response) => response.data);

export const fetchPools = async (filters?: any) => {
  const query = filters ? createParams(filters) : '';
  return instance.get(`pools?${query}`).then((response) => response.data);
};

export const createPool = async (record: CreatePool) =>
  instance.post('pools', record).then((response) => response.data);

export const fetchPool = async (id: number) =>
  instance.get(`pools/${id}`).then((response) => response.data);

export const fetchRegions = async (filters?: any) => {
  const query = filters ? createParams(filters) : '';
  return instance.get(`fixtures/regions?${query}`).then((response) => response.data);
};

export const fetchCompetitions = async (filters?: any) => {
  const query = filters ? createParams(filters) : '';
  return instance.get(`fixtures/competitions?${query}`).then((response) => response.data);
};

export const fetchMarkets = async (filters: any) => {
  const query = filters ? createParams(filters) : '';
  return instance.get(`market?${query}`).then((response) => response.data);
};

export const addPoolMarket = async (poolId: number, marketId: number) =>
  instance.post(`pools/markets/${poolId}/${marketId}`).then((response) => response.data);

export const deletePoolMarket = async (poolId: number, marketId: number) =>
  instance.delete(`pools/markets/${poolId}/${marketId}`).then((response) => response.data);

export const updatePool = async (poolId: number, data: UpdatePool) =>
  instance.patch(`pools/${poolId}`, data).then((response) => response.data);

export const deletePool = async (poolId: number) =>
  instance.delete(`pools/${poolId}`).then((response) => response.data);

export const fetchPoolBets = async (poolId: number, filters?: any) => {
  const query = filters ? createParams(filters) : '';
  return instance.get(`/pools/${poolId}/bets?${query}`).then((response) => response.data);
};

export const fetchPoolAvailableEvents = async (filters?: any) => {
  const query = filters ? createParams(filters) : '';
  return instance.get(`pools/available-events?${query}`).then((response) => response.data);
};
